/*
 * Copyright (C) 2022 NetBase Solutions, Inc.
 * 3960 Freedom Circle Suite 201 Santa Clara, CA 95054
 * All rights reserved.
 */

import React from 'react'
import { Widget } from 'features/types'
import { SocialWebStoryScopeWidget as SocialWebStoryScopeWidgetType } from 'schemas/SocialWebStoryScopeWidgetSchema'
import { useDashboardWidgetSetting } from 'hooks/useDashboardState'
import UnexpectedErrorBoundary from 'components/UnexpectedErrorBoundary'
import { getLabel } from 'utils/widgetTypeUtil'
import CountType from 'enums/CountType'

import StoryScopeWidget from './StoryScope'
import { resolveLevelOneInsights } from './utils'
import StoryScopeWidgetSettingGuards from './guards/StoryScopeWidgetSettingGuards'
import RunSearchEffect from './RunSearchEffect'
import { WidgetLoadingGuard } from '../share/guards'
import StoryScopeWidgetDataGuards from './guards/StoryScopeWidgetDataGuards'
import InsightModal from './InsightModal'
import useSelectedInsights from './InsightModal/hooks/useSelectedInsights'

const SocialWebStoryScopeWidget: Widget = ({ widgetId }) => {
  const widgetSetting = useDashboardWidgetSetting<SocialWebStoryScopeWidgetType>(widgetId)
  const {
    isModalOpen,
    selectedInsights,
    onClick,
    onClose,
    detailSetting,
    widgetSettingWithSelection
  } = useSelectedInsights(widgetSetting)

  return (
    <UnexpectedErrorBoundary>
      <StoryScopeWidgetSettingGuards widgetId={widgetId}>
        <RunSearchEffect widgetId={widgetId}>
          <WidgetLoadingGuard widgetId={widgetId}>
            <StoryScopeWidgetDataGuards widgetId={widgetId}>
              <StoryScopeWidget
                widgetId={widgetId}
                levelOneInsights={resolveLevelOneInsights(widgetSetting.levelOneType!)}
                onClick={onClick}
              />
            </StoryScopeWidgetDataGuards>
          </WidgetLoadingGuard>
        </RunSearchEffect>
      </StoryScopeWidgetSettingGuards>
      <InsightModal
        open={isModalOpen}
        onClose={onClose}
        selectedInsights={selectedInsights}
        title={widgetSetting?.name || getLabel(widgetSetting?.type)}
        widgetId={widgetId}
        detailSetting={detailSetting}
        countType={CountType.MENTIONS}
        widgetSettingWithSelection={widgetSettingWithSelection}
      />
    </UnexpectedErrorBoundary>
  )
}

export default SocialWebStoryScopeWidget
